import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import AppliedDiscountCard from "./AppliedDiscountCard";
import "./DiscountDrawer.css";

const QUICK_PERCENTAGES = [5, 10, 15, 20, 25, 50];
const PERCENTAGE_MAX = 100;

export default function DiscountDrawer({
  open,
  onClose,
  onApply,
  onRemove,
  cartTotal = 0,
  isApplying = false,
  appliedDiscountAmt = 0,
  appliedDiscountType = "NONE",
  locked = false,
}) {
  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [value, setValue] = useState("");

  const numericCartTotal = Number(cartTotal) || 0;
  const numericValue = parseFloat(value) || 0;
  const isPercentage = discountType === "PERCENTAGE";
  const hasAppliedDiscount = Number(appliedDiscountAmt) > 0;
  const isInteractionLocked = isApplying || hasAppliedDiscount || locked;

  const previewDiscount = isPercentage
    ? Math.min((numericValue / 100) * numericCartTotal, numericCartTotal)
    : Math.min(numericValue, numericCartTotal);
  const previewTotal = Math.max(numericCartTotal - previewDiscount, 0);
  const isFlatDiscountTooHigh =
    !isPercentage && numericCartTotal > 0 && numericValue > numericCartTotal;
  const canApply =
    numericValue > 0 && !isFlatDiscountTooHigh && !isInteractionLocked && numericCartTotal > 0;

  useEffect(() => {
    if (!open) {
      setDiscountType("PERCENTAGE");
      setValue("");
    }
  }, [open]);

  useEffect(() => {
    if (hasAppliedDiscount) {
      setValue("");
    }
  }, [hasAppliedDiscount]);

  if (!open) return null;

  const handleClose = () => {
    if (isApplying) return;
    onClose?.();
  };

  const handleTypeSwitch = (type) => {
    if (isInteractionLocked) return;
    setDiscountType(type);
    setValue("");
  };

  const handleQuickSelect = (percentage) => {
    if (isInteractionLocked) return;
    setDiscountType("PERCENTAGE");
    setValue(String(percentage));
  };

  const handleChange = (event) => {
    if (isInteractionLocked) return;

    let raw = event.target.value.replace(/[^0-9.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) {
      raw = `${parts[0]}.${parts.slice(1).join("")}`;
    }

    if (raw.length > 1 && raw[0] === "0" && raw[1] !== ".") {
      raw = raw.replace(/^0+/, "") || "0";
    }

    const nextValue = parseFloat(raw);

    if (isPercentage && !isNaN(nextValue) && nextValue > PERCENTAGE_MAX) return;
    setValue(raw);
  };

  const handleApply = () => {
    if (!canApply) return;

    onApply?.({
      discountType,
      discount: numericValue,
    });
  };

  return (
    <div className="discount-drawer-overlay" onClick={handleClose}>
      <div className="discount-drawer-panel" onClick={(event) => event.stopPropagation()}>
        <div className="discount-drawer-header">
          <div>
            <p className="discount-drawer-tag">CART ACTION</p>
            <h2 className="discount-drawer-title">Discounts</h2>
          </div>
          <button
            className="discount-drawer-close"
            onClick={handleClose}
            disabled={isApplying}
            type="button"
          >
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {hasAppliedDiscount && (
          <AppliedDiscountCard
            amount={appliedDiscountAmt}
            discountType={appliedDiscountType}
            onRemove={onRemove}
            loading={isApplying}
            locked={locked}
          />
        )}

        <div className="discount-type-toggle">
          <button
            className={`discount-toggle-btn ${isPercentage ? "active" : ""}`}
            onClick={() => handleTypeSwitch("PERCENTAGE")}
            disabled={isInteractionLocked}
            type="button"
          >
            Percentage
          </button>
          <button
            className={`discount-toggle-btn ${!isPercentage ? "active" : ""}`}
            onClick={() => handleTypeSwitch("FLAT_OFF")}
            disabled={isInteractionLocked}
            type="button"
          >
            Fixed Amount
          </button>
        </div>

        <div className="discount-input-section">
          <label className="discount-input-label">
            {isPercentage ? "Discount Percentage (%)" : "Discount Amount (Rs)"}
          </label>
          <div className={`discount-input-row ${isFlatDiscountTooHigh ? "error" : ""}`}>
            <input
              className="discount-input"
              type="text"
              inputMode="decimal"
              value={value}
              placeholder="0"
              onChange={handleChange}
              disabled={isInteractionLocked}
            />
            <span className="discount-input-suffix">{isPercentage ? "%" : "Rs"}</span>
          </div>
          {hasAppliedDiscount && (
            <span className="discount-input-hint">
              Remove the current discount before applying a new one
            </span>
          )}
          {isPercentage && (
            <span className="discount-input-hint">Max {PERCENTAGE_MAX}%</span>
          )}
          {!isPercentage && numericCartTotal > 0 && !isFlatDiscountTooHigh && (
            <span className="discount-input-hint">
              Max Rs {numericCartTotal.toFixed(2)}
            </span>
          )}
          {isFlatDiscountTooHigh && (
            <span className="discount-input-error">
              Discount cannot exceed the cart total
            </span>
          )}
        </div>

        {isPercentage && (
          <div className="discount-quick-pills">
            {QUICK_PERCENTAGES.map((percentage) => (
              <button
                key={percentage}
                className={`discount-pill ${value === String(percentage) ? "selected" : ""}`}
                onClick={() => handleQuickSelect(percentage)}
                disabled={isInteractionLocked}
                type="button"
              >
                {percentage}%
              </button>
            ))}
          </div>
        )}

        <div className="discount-preview">
          <div className="discount-preview-row">
            <span className="discount-preview-label">Cart Total</span>
            <span className="discount-preview-value">Rs {numericCartTotal.toFixed(2)}</span>
          </div>

          {numericValue > 0 && !isFlatDiscountTooHigh && (
            <>
              <div className="discount-preview-divider" />
              <div className="discount-preview-row">
                <span className="discount-preview-label">Discount</span>
                <span className="discount-preview-value discount-preview-saving">
                  - Rs {previewDiscount.toFixed(2)}
                </span>
              </div>
              <div className="discount-preview-divider" />
              <div className="discount-preview-row">
                <span className="discount-preview-label discount-preview-total-label">
                  New Total
                </span>
                <span className="discount-preview-value discount-preview-total-value">
                  Rs {previewTotal.toFixed(2)}
                </span>
              </div>
            </>
          )}
        </div>

        <button
          className={`discount-apply-btn ${!canApply ? "disabled" : ""}`}
          onClick={handleApply}
          disabled={!canApply}
          type="button"
        >
          {isApplying ? "Applying..." : "Apply Discount"}
        </button>
      </div>
    </div>
  );
}
